import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Flex, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { BarFill, BarTrack } from 'pages/HrDashboard/hrLayout.styled'
import { getAnalyticsLosses, getShiftReview } from 'features/operations/opsAnalyticsSlice'
import { AppDispatch, RootState } from 'store'
import { IOpsLossPareto, OpsLossBucket } from 'types/opsAnalytics.interface'
import { LOSS_BUCKET_META } from '../Shared/lossClassifier'
import { LOSS_BUCKET_TO_UI } from '../Shared/opsEnumMaps'
import { LossBucket } from 'types/operationsEnums.interface'
import { LossWaterfall } from './LossWaterfall'
import { DefinitionHint, FootnoteWrap, HoursCellWrap, SectionStack } from './Losses.styled'

interface IParetoRow extends IOpsLossPareto {
  id: number
}

const bucketFromKey = (key: string): OpsLossBucket => {
  const numeric = Number(key)
  if (!Number.isNaN(numeric) && LOSS_BUCKET_TO_UI[numeric]) {
    return LOSS_BUCKET_TO_UI[numeric] as OpsLossBucket
  }
  const match = (Object.values(LossBucket) as number[]).find((v) => LOSS_BUCKET_TO_UI[v] === key)
  return match != null ? (LOSS_BUCKET_TO_UI[match] as OpsLossBucket) : 'NO_TASK'
}

const paretoColumns = (maxHours: number): IHonestTableColumn<IParetoRow>[] => [
  {
    id: 'bucket',
    header: 'Bucket',
    cell: (item) => (
      <DefinitionHint title={LOSS_BUCKET_META[item.bucket].definition}>
        <StatusPill status={LOSS_BUCKET_META[item.bucket].tone}>
          {LOSS_BUCKET_META[item.bucket].label}
        </StatusPill>
      </DefinitionHint>
    ),
  },
  {
    id: 'hours',
    header: 'Hours',
    minWidth: '14rem',
    cell: (item) => (
      <HoursCellWrap>
        <BodyText weight='700'>{item.hours} h</BodyText>
        <BarTrack>
          <BarFill
            $pct={(item.hours / maxHours) * 100}
            $color={tokens.status[LOSS_BUCKET_META[item.bucket].tone].fg}
          />
        </BarTrack>
      </HoursCellWrap>
    ),
  },
  {
    id: 'cost',
    header: 'Cost',
    cell: (item) =>
      item.costGhs != null ? (
        <BodyText>GH₵ {item.costGhs.toLocaleString('en-US')}</BodyText>
      ) : (
        <OtherText grey>—</OtherText>
      ),
  },
  {
    id: 'topContributor',
    header: 'Top contributor',
    cell: (item) => <BodyText>{item.topContributor}</BodyText>,
  },
]

export const Losses = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { losses, shiftReview, loaded, isLoading } = useSelector((s: RootState) => s.opsAnalytics)

  useEffect(() => {
    const shiftDate = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
    dispatch(getAnalyticsLosses({ groupBy: 'bucket' }))
    dispatch(getShiftReview({ shiftDate }))
  }, [dispatch])

  const paretoRows: IParetoRow[] = (losses?.items ?? []).map((item, index) => ({
    id: index + 1,
    bucket: bucketFromKey(item.key),
    hours: item.hours,
    costGhs: item.costValue,
    topContributor: item.key,
  }))

  const maxParetoHours = Math.max(...paretoRows.map((row) => row.hours), 1)

  const waterfallBars =
    shiftReview?.waterfall.buckets.map((b) => ({
      label: LOSS_BUCKET_META[LOSS_BUCKET_TO_UI[b.bucket] as OpsLossBucket]?.label ?? String(b.bucket),
      tonnes: b.costValue,
      bucket: (LOSS_BUCKET_TO_UI[b.bucket] ?? 'NO_TASK') as OpsLossBucket | 'TARGET' | 'ACTUAL' | 'UNATTRIBUTED',
      drillPath: null,
    })) ?? []

  if (loaded && !isLoading && !paretoRows.length && !waterfallBars.length) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Losses
          </Head1>
          <NoData text='Loss accounting starts once shift aggregates are computed at shift close.' />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex center gap='0.75rem'>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Losses
          </Head1>
        </Flex>
        <Flex mt='4'>
          <BodyText grey>Every lost tonne attributed to a cause — tap a bar to see the evidence.</BodyText>
        </Flex>
        <SectionStack>
          <Container>
            <ContainerHeader>
              <TitleText>Latest shift waterfall</TitleText>
            </ContainerHeader>
            <ContainerContent>
              {waterfallBars.length ? (
                <LossWaterfall bars={waterfallBars} />
              ) : (
                <NoData text='Waterfall appears after the first finalized shift.' />
              )}
            </ContainerContent>
          </Container>
          <Container>
            <ContainerHeader>
              <TitleText>Where the hours go</TitleText>
            </ContainerHeader>
            <ContainerContent>
              {paretoRows.length ? (
                <>
                  <HonestTable columns={paretoColumns(maxParetoHours)} data={paretoRows} keyField='id' />
                  <FootnoteWrap>
                    <OtherText grey>
                      Costing: breakdown hours × machine rate — definitions in each tooltip.
                    </OtherText>
                  </FootnoteWrap>
                </>
              ) : (
                <NoData text='Pareto data appears once loss buckets are materialized.' />
              )}
            </ContainerContent>
          </Container>
        </SectionStack>
      </PageWrapper>
    </>
  )
}
